import React from 'react';
import './WalletPreview.css';
import { Users, ArrowRight, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Contact {
  id?: string;
  contactId?: string;
  displayName?: string;
  display_name?: string;
  name?: string;
  email?: string;
  companyName?: string;
  company_name?: string;
  company?: string;
  addedAt?: string;
  added_at?: string;
  createdAt?: string;
  photoUrl?: string;
  photo_url?: string;
  [key: string]: any;
}

interface WalletPreviewProps {
  contacts: Contact[];
  totalCount: number;
}

const WalletPreview: React.FC<WalletPreviewProps> = ({ contacts, totalCount }) => {
  const navigate = useNavigate();

  const handleViewAll = () => {
    navigate('/wallet');
  };

  const getContactName = (contact: Contact): string => {
    return contact.displayName || contact.display_name || contact.name || 'Unknown';
  };

  const getContactCompany = (contact: Contact): string => {
    return contact.companyName || contact.company_name || contact.company || contact.email || 'No company';
  };

  const getContactId = (contact: Contact): string => {
    return contact.id || contact.contactId || String(Math.random());
  };

  const getContactPhoto = (contact: Contact): string | undefined => {
    return contact.photoUrl || contact.photo_url;
  };

  const getContactDate = (contact: Contact): string => {
    return contact.addedAt || contact.added_at || contact.createdAt || new Date().toISOString();
  };

  const getTimeAgo = (dateString: string) => {
    if (!dateString) return 'Recently';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
    return `${Math.floor(diffInDays / 30)} months ago`;
  };

  return (
    <div className="wallet-preview">
      <div className="wallet-preview-header">
        <div className="wallet-preview-title">
          <Users size={18} />
          <h3>Recent Contacts</h3>
          <span className="wallet-count-badge">{totalCount}</span>
        </div>
        <button onClick={handleViewAll} className="btn-view-all">
          View All
          <ArrowRight size={14} />
        </button>
      </div>

      <div className="wallet-preview-content">
        {contacts.length === 0 ? (
          <div className="wallet-empty-state">
            <Users size={32} />
            <p>No contacts yet</p>
            <span>Scan business cards to add contacts</span>
          </div>
        ) : (
          <div className="contacts-list">
            {contacts.slice(0, 3).map((contact, index) => {
              const contactId = getContactId(contact);
              const contactName = getContactName(contact);
              const contactCompany = getContactCompany(contact);
              const contactPhoto = getContactPhoto(contact);
              const contactDate = getContactDate(contact);
              
              return (
                <div key={contactId || index} className="contact-preview-item" onClick={() => navigate('/wallet', { state: { selectedContactId: contactId } })}>
                  <div className="contact-avatar">
                    {contactPhoto ? (
                      <img src={contactPhoto} alt={contactName} />
                    ) : (
                      <div className="avatar-placeholder">
                        <User size={18} />
                      </div>
                    )}
                  </div>
                  <div className="contact-info">
                    <h4>{contactName}</h4>
                    <p>{contactCompany}</p>
                  </div>
                  <span className="contact-time">{getTimeAgo(contactDate)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default WalletPreview;
